import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayDisabledIcon from '@mui/icons-material/PlayDisabled'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ImageIcon from '@mui/icons-material/Image'
import { CircularProgress, Box, Typography, Link } from '@mui/material'
import { useSpeech } from '../../context/speechSynthesisProvider'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'

type ImageEvent = {
  imageUrl: string
  concept: string
  description: string
  messageId: number
  timestamp: number
}

type Props = {
  message: string
  loadingResponse: boolean
  messageId: number | undefined
  showSpinner?: boolean
  showPlayButton?: boolean
  images?: ImageEvent[]
  toolGeneratingImage?: boolean
}

/**
 * Strips markdown formatting and emojis from text for TTS (text-to-speech) processing.
 * This ensures the voice synthesis reads clean text without pronouncing markdown syntax.
 *
 * @param text - The markdown-formatted text
 * @returns Plain text without markdown formatting or emojis
 */
function stripMarkdownAndEmojis(text: string): string {
  let cleaned = text

  // Remove markdown links: [text](url) -> text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

  // Remove markdown images: ![alt](url) -> empty
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '')

  // Remove headings: # Heading -> Heading
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '')

  // Remove bold: **text** or __text__ -> text
  cleaned = cleaned.replace(/(\*\*|__)(.*?)\1/g, '$2')

  // Remove italic: *text* or _text_ -> text
  cleaned = cleaned.replace(/(\*|_)(.*?)\1/g, '$2')

  // Remove strikethrough: ~~text~~ -> text
  cleaned = cleaned.replace(/~~(.*?)~~/g, '$1')

  // Remove inline code: `code` -> code
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1')

  // Remove code blocks: ```code``` -> code
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '')

  // Remove blockquote markers: > text -> text
  cleaned = cleaned.replace(/^>\s+/gm, '')

  // Remove list markers: - item or * item or + item or 1. item -> item
  cleaned = cleaned.replace(/^[\s]*[-*+]\s+/gm, '')
  cleaned = cleaned.replace(/^[\s]*\d+\.\s+/gm, '')

  // Remove horizontal rules: --- or *** or ___
  cleaned = cleaned.replace(/^[-*_]{3,}$/gm, '')

  // Remove emojis (comprehensive emoji regex)
  cleaned = cleaned.replace(
    /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE0F}]/gu,
    ''
  )

  // Clean up multiple spaces and newlines
  cleaned = cleaned.replace(/\s+/g, ' ')

  // Trim whitespace
  cleaned = cleaned.trim()

  return cleaned
}

/**
 * BotMessage Component
 *
 * Renders messages from the chatbot with markdown formatting support.
 * Supports: bold, italic, headings, lists, links, blockquotes
 *
 * Examples of supported markdown:
 * - **Bold text** or __bold text__
 * - *Italic text* or _italic text_
 * - # Heading 1, ## Heading 2, etc.
 * - Ordered lists: 1. First item, 2. Second item
 * - Unordered lists: - Item, * Item
 * - Links: [Text](https://example.com)
 * - Blockquotes: > Quote text
 */
function BotMessage({
  message,
  loadingResponse,
  messageId,
  showSpinner = false,
  showPlayButton = false,
  images = [],
  toolGeneratingImage = false
}: Props) {
  const { speak, pause, isPlaying, isPlayingAnotherMessage } = useSpeech()

  return (
    <>
      {(loadingResponse || showSpinner) && <CircularProgress />}
      {!loadingResponse && (
        <Box sx={{ textAlign: 'left' }}>
          {/* Render markdown-formatted message */}
          <ReactMarkdown
            rehypePlugins={[rehypeSanitize]}
            components={{
              // Map markdown elements to MUI components for consistent styling
              p: ({ children }) => (
                <Typography variant="body1" component="p" sx={{ mb: 1 }}>
                  {children}
                </Typography>
              ),
              h1: ({ children }) => (
                <Typography variant="h4" component="h1" sx={{ mt: 2, mb: 1 }}>
                  {children}
                </Typography>
              ),
              h2: ({ children }) => (
                <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
                  {children}
                </Typography>
              ),
              h3: ({ children }) => (
                <Typography variant="h6" component="h3" sx={{ mt: 1.5, mb: 1 }}>
                  {children}
                </Typography>
              ),
              h4: ({ children }) => (
                <Typography variant="subtitle1" component="h4" sx={{ mt: 1.5, mb: 0.5, fontWeight: 'bold' }}>
                  {children}
                </Typography>
              ),
              h5: ({ children }) => (
                <Typography variant="subtitle2" component="h5" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                  {children}
                </Typography>
              ),
              h6: ({ children }) => (
                <Typography variant="body1" component="h6" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                  {children}
                </Typography>
              ),
              a: ({ href, children }) => (
                <Link href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </Link>
              ),
              ul: ({ children }) => (
                <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 3 }}>
                  {children}
                </Box>
              ),
              ol: ({ children }) => (
                <Box component="ol" sx={{ mt: 0.5, mb: 1, pl: 3 }}>
                  {children}
                </Box>
              ),
              li: ({ children }) => (
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  {children}
                </Typography>
              ),
              blockquote: ({ children }) => (
                <Box
                  component="blockquote"
                  sx={{
                    borderLeft: 4,
                    borderColor: 'grey.400',
                    pl: 2,
                    py: 0.5,
                    my: 1,
                    fontStyle: 'italic',
                    color: 'text.secondary'
                  }}
                >
                  {children}
                </Box>
              ),
              strong: ({ children }) => <strong>{children}</strong>,
              em: ({ children }) => <em>{children}</em>
            }}
          >
            {message}
          </ReactMarkdown>

          {/* Render images */}
          {images.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {images.map((imageEvent, index) => (
                <Box key={`${imageEvent.timestamp}-${index}`} sx={{ mb: 2 }}>
                  <img
                    src={imageEvent.imageUrl}
                    alt={imageEvent.concept}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '8px',
                      display: 'block',
                      margin: '8px 0'
                    }}
                    onError={(e) => {
                      console.error('Image failed to load:', imageEvent.imageUrl)
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                  {/* {imageEvent.description && ( */}
                  {/*   <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}> */}
                  {/*     {imageEvent.description} */}
                  {/*   </Typography> */}
                  {/* )} */}
                </Box>
              ))}
            </Box>
          )}

          {/* Show image generation in progress */}
          {toolGeneratingImage && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <ImageIcon sx={{ mr: 1 }} />
              <Typography variant="caption">Afbeelding wordt gegenereerd...</Typography>
            </Box>
          )}

          {/* Audio controls */}
          {showPlayButton && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              {isPlayingAnotherMessage(messageId || 0) && <PlayDisabledIcon />}
              {!isPlayingAnotherMessage(messageId || 0) && (
                <>
                  {isPlaying(messageId || 0) && (
                    <PauseCircleOutlineIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        pause(messageId || 0)
                      }}
                    />
                  )}
                  {!isPlaying(messageId || 0) && (
                    <PlayCircleOutlineIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => speak(stripMarkdownAndEmojis(message), messageId || 0)}
                    />
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  )
}

export default BotMessage
